import React, { useState } from "react";
import {
  useGetPlatformPhoneNumbersQuery,
  useRegisterNumberReputationMutation,
  useFlagNumberReputationMutation,
  useAppealNumberReputationMutation,
  useRefreshOrganisationReputationMutation,
} from "@/store/admin_api";
import PhoneNumber from "@/model/interface/PhoneNumber";
import { apiErrorMessage } from "@/util/apiError";
import { formatDate } from "@/util/date";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FaEllipsisH } from "react-icons/fa";
import { toast } from "sonner";

const REPUTATION_BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  REGISTERED: "default",
  PENDING: "outline",
  APPEALING: "outline",
  FLAGGED: "destructive",
};

export default function PhoneNumbersList() {
  const { data: numbers, isLoading } = useGetPlatformPhoneNumbersQuery();
  const [registerReputation] = useRegisterNumberReputationMutation();
  const [flagReputation] = useFlagNumberReputationMutation();
  const [appealReputation] = useAppealNumberReputationMutation();
  const [refreshReputation] = useRefreshOrganisationReputationMutation();
  const [search, setSearch] = useState("");

  const filteredNumbers = numbers?.filter((phoneNumber) => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return [phoneNumber.number, phoneNumber.alias, phoneNumber.organisation?.name]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(term));
  });

  const runAction = async (action: () => Promise<unknown>, successMessage: string, failureMessage: string) => {
    try {
      await action();
      toast.success(successMessage);
    } catch (error) {
      toast.error(apiErrorMessage(error, failureMessage));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Phone Numbers</h2>
        <p className="text-muted-foreground text-sm">
          Every number on the platform with its texting enrollment and caller reputation state.
        </p>
      </div>

      <Input
        placeholder="Search by number, alias or organisation"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="max-w-sm"
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organisation</TableHead>
            <TableHead>Number</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Active</TableHead>
            <TableHead>SMS Enrollment</TableHead>
            <TableHead>Reputation</TableHead>
            <TableHead>Reputation Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!filteredNumbers?.length && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                No phone numbers found
              </TableCell>
            </TableRow>
          )}
          {filteredNumbers?.map((phoneNumber: PhoneNumber) => (
            <TableRow key={phoneNumber.id}>
              <TableCell className="font-medium">{phoneNumber.organisation?.name || "-"}</TableCell>
              <TableCell>{phoneNumber.alias ? `${phoneNumber.alias} (${phoneNumber.number})` : phoneNumber.number}</TableCell>
              <TableCell>{phoneNumber.type || "-"}</TableCell>
              <TableCell>{phoneNumber.active ? "Yes" : "No"}</TableCell>
              <TableCell>
                <Badge variant={phoneNumber.a2pEnrollmentStatus === "ENROLLED" ? "default" : "secondary"}>
                  {(phoneNumber.a2pEnrollmentStatus || "NOT ENROLLED").replace(/_/g, " ")}
                </Badge>
              </TableCell>
              <TableCell>
                {phoneNumber.reputationStatus ? (
                  <Badge variant={REPUTATION_BADGE_VARIANTS[phoneNumber.reputationStatus] || "secondary"}>
                    {phoneNumber.reputationStatus}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                {phoneNumber.reputationUpdatedAt ? formatDate(phoneNumber.reputationUpdatedAt) : "-"}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost">
                      <FaEllipsisH className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() =>
                        runAction(
                          () => registerReputation(phoneNumber.id).unwrap(),
                          "Reputation registration submitted",
                          "Failed to register the number",
                        )
                      }
                    >
                      Register reputation
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        runAction(
                          () => flagReputation(phoneNumber.id).unwrap(),
                          "Marked as spam flagged",
                          "Failed to flag the number",
                        )
                      }
                    >
                      Mark spam flagged
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        runAction(
                          () => appealReputation(phoneNumber.id).unwrap(),
                          "Appeal submitted to the carrier",
                          "Failed to submit the appeal",
                        )
                      }
                    >
                      Appeal spam label
                    </DropdownMenuItem>
                    {phoneNumber.organisation?.id && (
                      <DropdownMenuItem
                        onClick={() =>
                          runAction(
                            () => refreshReputation(phoneNumber.organisation!.id!).unwrap(),
                            "Reputation statuses refreshed",
                            "Failed to refresh reputation statuses",
                          )
                        }
                      >
                        Refresh org statuses
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
